import React from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
// Modules
import { actions as billActions, types as billTypes } from '../../../redux/reducers/bills';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { selectors as errorSelectors, ErrorPayload } from '../../../redux/reducers/errors';
import { AccountRight, RightRank } from '../../../utils/userUtils';
import { CreditNoteType } from '../../../redux/reducers/api-types';
// Ressources
import { paths } from '../../paths';
import { FormDatas, FormPickerDatas } from '../../../local-types';
const queryString = require('query-string');

interface CreditAddProps extends WithTranslation, RouteComponentProps {
    saveCreditError: ErrorPayload;
    saveCredit: (payload: any, callback: React.EffectCallback) => any;
    isSavingCredit: boolean;
}

interface CreditAddState {
    formData: any;
}

interface CreditAddPayload {
    label: string;
    customer_bill_id: string;
    with_tax_customer: number;
    product_type?: 'adjustment_transport' | 'adjustment_service';
}

interface CreditAddFormData {
    id: string;
    label: string;
    bill_number: string;
    with_tax_customer: number;
    motif: 'default' | 'adjustment_transport' | 'adjustment_service';
}

class CreditAddPage extends React.Component<CreditAddProps, CreditAddState> {
    inputs: FormDatas[];
    optionsMotif: FormPickerDatas[];
    filterParams: any;

    constructor(props: CreditAddProps) {
        super(props);
        const { t } = props;

        this.filterParams = queryString.parse(this.props.location.search);
        const { bill_number, id, type } = this.filterParams;

        this.state = {
            formData: {
                id,
                bill_number,
            },
        };

        this.optionsMotif = [
            {
                id: 'default',
                name: t('credit.type.default'),
            },
            {
                id: 'adjustment_transport',
                name: t('credit.type.adjustment_transport'),
            },
            {
                id: 'adjustment_service',
                name: t('credit.type.adjustment_service'),
            },
        ];

        this.inputs = [
            {
                title: t('credit.new'),
                datas: [
                    {
                        id: 'bill_number',
                        label: t('bill_id'),
                        required: true,
                        disable: true,
                    },
                    { id: 'label', label: t('reason'), required: true },
                    type !== CreditNoteType.EXCEPTIONAL && {
                        id: 'motif',
                        label: t('credit.type.title'),
                        type: 'picker',
                        options: this.optionsMotif,
                        hint: t('credit.hint'),
                        noEmpty: true,
                        required: true,
                    },
                    {
                        id: 'with_tax_customer',
                        label: `${t('credit.amount_refound_with_vat')} (${t('common:euro_ttc')})`,
                        type: 'number',
                        numberNegative: false,
                        required: true,
                    },
                ],
            },
        ];
    }

    submitExceptional = (formData: CreditAddFormData): void => {
        const { saveCredit, history } = this.props;
        const { type } = this.filterParams;

        saveCredit(this.generatePayload(formData), () => {
            if (type === CreditNoteType.MONTH) {
                history.push(paths.billsMonthCreditNotes);
            } else if (type === CreditNoteType.EXCEPTIONAL) {
                history.push(paths.billsExceptionalCreditNotes);
            } else {
                history.push(paths.billsCreditNotes);
            }
        });
    };

    generatePayload = (formData: CreditAddFormData): CreditAddPayload => {
        const payload: CreditAddPayload = {
            label: formData.label,
            customer_bill_id: formData.id,
            ...(formData.motif !== 'default' && { product_type: formData.motif }),
            with_tax_customer: -Math.abs(formData.with_tax_customer),
        };

        return payload;
    };

    handleSubmit = (formData, event): void => {
        this.setState({
            formData: formData,
        });
        this.submitExceptional(formData);
    };

    render() {
        const { saveCreditError, isSavingCredit } = this.props;
        const formPayload = this.state.formData;

        return (
            <App needRights={{ name: AccountRight.CREDIT_NOTE_MGMT, mode: RightRank.W }}>
                <ErrorMessage payload={saveCreditError} />
                <>
                    <EnhancedForm
                        payload={formPayload}
                        inputs={this.inputs}
                        backRoute={paths.billsCreditNotes}
                        onSubmit={this.handleSubmit}
                        loader={isSavingCredit}
                    />
                </>
            </App>
        );
    }
}

const CreditAddPageWithStyleAndTranslation = withTranslation(['bill', 'common'])(CreditAddPage);

const mapStateToProps = (state) => ({
    saveCreditError: errorSelectors.saveCreditError(state),
    isSavingCredit: pendingSelectors.isPending(state, billTypes.CREATE_BILLS_CREDIT),
});

const mapDispatchToProps = {
    saveCredit: billActions.saveCredit,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreditAddPageWithStyleAndTranslation);
